import i18n from 'i18n/config';
import { humanize } from 'common/helpers/humanize';

const MAX_TAG_TYPE_ID = 16;
const TAG_TYPE_NAMESPACE = 'tagTemplates.tagTypes';
const TAG_TYPE_SLUG_NAMESPACE = 'diagnosticRules.form.tagTypeOptions';

function toSlug(str) {
  return String(str)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function translationExists(t, key) {
  const value = t(key);
  return value !== key;
}

function tryTagTypeKey(t, key) {
  if (key == null || key === '') return null;

  const stringKey = String(key);
  const primaryKey = `${TAG_TYPE_NAMESPACE}.${stringKey}`;
  if (translationExists(t, primaryKey)) {
    return t(primaryKey);
  }

  const slug = toSlug(stringKey);
  const slugKey = `${TAG_TYPE_NAMESPACE}.${slug}`;
  if (translationExists(t, slugKey)) {
    return t(slugKey);
  }

  const diagnosticKey = `${TAG_TYPE_SLUG_NAMESPACE}.${slug}`;
  if (translationExists(t, diagnosticKey)) {
    return t(diagnosticKey);
  }

  return null;
}

function resolveTagTypeIdByEnglishName(name) {
  if (!name) return null;
  const enT = i18n.getFixedT('en');
  for (let id = 1; id <= MAX_TAG_TYPE_ID; id += 1) {
    if (enT(`${TAG_TYPE_NAMESPACE}.${id}`, { defaultValue: '' }) === name) {
      return String(id);
    }
  }
  return null;
}

/**
 * Localized tag type label for tag templates and related views.
 *
 * @param {function} t - i18next `t`
 * @param {object} options
 * @param {string} [options.tagType] - API tag type text or slug (e.g. `Temperature`, `current`)
 * @param {number|string} [options.tagTypeId] - Numeric tag type id when available
 * @param {Array<{ text?: string, value?: string|number }>} [options.metadataTagTypes=[]]
 * @returns {string}
 */
export function getTagTypeDisplayName(t, { tagType, tagTypeId, metadataTagTypes = [] } = {}) {
  if (tagTypeId != null && tagTypeId !== '') {
    const byId = tryTagTypeKey(t, tagTypeId);
    if (byId) return byId;
  }

  if (tagType && metadataTagTypes.length) {
    const matched = metadataTagTypes.find(
      tt => tt.text === tagType
        || tt.value === tagType
        || String(tt.value) === String(tagType)
    );
    if (matched) {
      const fromMetadata = tryTagTypeKey(t, matched.value);
      if (fromMetadata) return fromMetadata;
    }
  }

  if (tagType) {
    const direct = tryTagTypeKey(t, tagType);
    if (direct) return direct;

    const resolvedId = resolveTagTypeIdByEnglishName(tagType);
    if (resolvedId) {
      const byEnglishName = tryTagTypeKey(t, resolvedId);
      if (byEnglishName) return byEnglishName;
    }

    return humanize(tagType);
  }

  return '';
}
